import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useState,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, ImagePlus, Trash2, Upload, X } from "lucide-react";
import { DateSelectArg } from "@fullcalendar/core";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import moment from "moment";
import { z } from "zod";

import {
	createEvent,
	deleteEvent,
	exportEvent,
	updateEvent,
} from "../../Api/api";
import {
	areArraysEqual,
	cn,
	convertHTMLToPlainText,
	copyToClipboard,
	emailSchema,
	formatPhoneNumber,
	formatPhoneNumberWhileTyping,
	isHTML,
	phoneNumberSchema,
} from "../../Utils/Utils";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import { Button } from "../ui/button";
import { InputType, User } from "../../Utils/Types";
import ConfirmationModal from "./ConfirmationModal";
import ImageTile from "../Reusable/ImageTile/ImageTile";
import TiptapRichTextEditor from "../RichText/RichText";
import { EventType, Images } from "../Calendar/Calendar";

const contractors = [
	{ name: "Jose Castro", phoneNumber: "16502060041" },
	{ name: "Joe", phoneNumber: "12148363773" },
	{ name: "Rooster", phoneNumber: "12144347834" },
	{ name: "Hamza", phoneNumber: "17472920712" },
];

const colors = {
	lessThanTwoHours: "#4fbaff",
	twoHours: "#4997fc",
	fourHours: "#98c3fa",
	sixHours: "#c8c2ff",
	eightHours: "#9487ff",
	moreThanEightHours: "#a968f2",
};

const CreateEventModal = ({
	open,
	closePopup,
	events,
	setEvents,
	authenticatedUser,
}: {
	authenticatedUser: User | null;
	open: null | {
		open: boolean;
		event: DateSelectArg;
		id?: string;
		edit?: boolean;
	};
	closePopup: () => void;
	events: EventType[];
	setEvents: Dispatch<SetStateAction<EventType[]>>;
}) => {
	const [exportMode, setExportMode] = useState<boolean>(false);
	const [selectUser, setSelectUser] = useState<{
		name: string;
		phoneNumber: string;
	} | null>(null);
	const [name, setName] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [contractorName, setContractorName] = useState<string>("");
	const [notes, setNotes] = useState<string>("");
	const [phoneNumber, setPhoneNumber] = useState<string>("");
	const [address, setAddress] = useState<string>("");
	const [images, setImages] = useState<Images[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
	const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
	const [copyUnsavedChanges, setCopyUnsavedChanges] = useState<boolean>(false);
	const [closeUnsavedChanges, setCloseUnsavedChanges] =
		useState<boolean>(false);
	const [files, setFiles] = useState<File[]>([]);
	const [timeSlot, setTimeSlot] = useState<
		"2 Hours" | "4 Hours" | "6 Hours" | "8 Hours" | null
	>(null);
	const [error, setError] = useState<{
		email: string | null;
		phone: string | null;
	}>({ email: null, phone: null });

	const handleClose = (check = true) => {
		const event = events.find((item) => item._id === open?.id);
		if (
			check &&
			(name !== (event?.title ?? "") ||
				contractorName !== (event?.contractorName ?? "") ||
				notes !== (event?.notes ?? "") ||
				phoneNumber !== (event?.phoneNumber ?? "") ||
				address !== (event?.address ?? "") ||
				!areArraysEqual(images, event?.files ?? []) ||
				!areArraysEqual(event?.files ?? [], images) ||
				files?.length > 0 ||
				timeSlot !== (event?.timeSlot ?? null))
		) {
			setCloseUnsavedChanges(true);
			return;
		}
		setName("");
		setEmail("");
		setNotes("");
		setContractorName("");
		setPhoneNumber("");
		setAddress("");
		setExportMode(false);
		setSelectUser(null);
		setTimeSlot(null);
		setFiles([]);
		setImages([]);
		setError({ email: null, phone: null });
		closePopup();
	};

	useEffect(() => {
		if (open?.id && open?.edit && events.length > 0) {
			const event = events.find((item) => item.id === open?.id);
			if (!event) return;
			setName(event?.title);
			// setEmail(event?.email);
			setNotes(event?.notes);
			setContractorName(event?.contractorName);
			setPhoneNumber(event?.phoneNumber);
			setAddress(event?.address);
			setTimeSlot(event?.timeSlot);
			setImages(event?.files);
		}
	}, [open?.id, open?.edit, events, open]);

	const addEvent = async (data: FormData) => {
		const response = await createEvent(data);
		if (response?.status === 200) return response.data;
	};
	const editEvent = async (data: FormData) => {
		if (!open?.id) return;
		const response = await updateEvent(open?.id, data);
		if (response?.status === 200) return response.data;
	};

	const handleTextChange = (type: InputType) => (value: string) => {
		try {
			if (type === InputType.email) {
				setEmail(value);
				emailSchema.parse(value);
			} else if (type === InputType.phone) {
				setPhoneNumber(formatPhoneNumberWhileTyping(value));
				phoneNumberSchema.parse(value);
			}
			if (type === InputType.email || type === InputType.phone)
				setError({ ...error, [type]: null });
		} catch (err) {
			if (err instanceof z.ZodError) {
				if (type === InputType.email)
					setError({ ...error, email: err.errors[0].message });
				else if (type === InputType.phone)
					setError({ ...error, phone: err.errors[0].message });
			}
		}
	};

	const canContinue = () => {
		return (
			!loading &&
			name?.trim() &&
			(!email?.trim() || !error?.email) &&
			(!phoneNumber?.trim() || (phoneNumber.length >= 8 && !error?.phone))
		);
	};

	const selectFiles = (newFiles: File[]) =>
		setFiles((prev) => [...prev, ...newFiles]);

	const getEndTime = () => {
		const slot = timeSlot
			? timeSlot.includes("2")
				? 2
				: timeSlot.includes("4")
				? 4
				: timeSlot.includes("6")
				? 6
				: timeSlot.includes("8") && 8
			: null;
		let end;
		if (slot) end = moment(open?.event?.start).add(slot, "hours");
		else end = moment(open?.event?.end);
		return end;
	};

	const isSlotLimitExceeded = (
		start: moment.Moment,
		end: moment.Moment,
		events: EventType[],
		excludeId?: string
	) => {
		const MAX_EVENTS_PER_SLOT = 3;
		const slotDuration = 60; // in minutes

		for (
			let slotStart = start.clone();
			slotStart.isBefore(end);
			slotStart.add(slotDuration, "minutes")
		) {
			const slotEnd = slotStart.clone().add(slotDuration, "minutes");
			let overlappingInSlot = 0;

			for (const event of events) {
				if (event._id === excludeId) continue;

				const eventStart = moment(event.start);
				const eventEnd = moment(event.end);

				if (slotStart.isBefore(eventEnd) && slotEnd.isAfter(eventStart)) {
					overlappingInSlot++;
				}
			}

			// Including the new event itself
			overlappingInSlot++;

			if (overlappingInSlot > MAX_EVENTS_PER_SLOT) {
				return true;
			}
		}

		return false;
	};

	const handleSubmit = async ({ close = true }: { close?: boolean }) => {
		const formData = new FormData();
		const authenticatedUser = sessionStorage.getItem("authenticatedUser");
		if (!authenticatedUser) return;

		const user: User = JSON.parse(authenticatedUser);
		const userId = user?._id;
		const userName = user?.name;
		const nickName = user?.nick_name;
		if (!userId || !userName || !nickName || !canContinue()) return;

		const start = moment(open?.event?.start);
		const end = getEndTime();

		// 🔒 Slot Overlap Check
		if (isSlotLimitExceeded(start, end, events, open?.id)) {
			toast.error("Error", {
				description:
					"One or more time slots already have 3 jobs scheduled. Please choose another.",
			});
			return;
		}

		setLoading(true);

		let eventColor = colors.lessThanTwoHours;
		const duration = moment.duration(end.diff(start)).asHours();

		if (duration >= 2 && duration < 4) eventColor = colors.twoHours;
		else if (duration >= 4 && duration < 6) eventColor = colors.fourHours;
		else if (duration >= 6 && duration < 8) eventColor = colors.sixHours;
		else if (duration === 8) eventColor = colors.eightHours;
		else if (duration > 8) eventColor = colors.moreThanEightHours;

		const newEvent: EventType = {
			title: name,
			start: start.toISOString(),
			end: end?.toISOString(),
			notes,
			address,
			contractorName,
			phoneNumber: formatPhoneNumber(phoneNumber),
			timeSlot,
			color: eventColor,
			userId,
			userName,
			files: images,
			userNickname: nickName,
		};

		(Object.keys(newEvent) as (keyof EventType)[]).forEach((key) => {
			const value = newEvent[key];
			formData.append(
				key,
				key === "timeSlot" && value === null
					? "null"
					: key === "files"
					? JSON.stringify(value)
					: value
					? String(value)
					: ""
			);
		});

		if (files.length > 0) {
			files.forEach((file) => {
				formData.append("files", file);
			});
		}

		if (open?.edit) {
			const res = await editEvent(formData);
			if (res.code === 200) {
				toast("Success", {
					description: "Job updated successfully.",
				});
				setEvents((prevEvents) =>
					[...prevEvents].map((item) =>
						item.id === open?.id ? { ...res.data, id: res.data._id } : item
					)
				);
				if (copyUnsavedChanges) {
					setCopyUnsavedChanges(false);
					await copyEventToClipboard({ ...res.data, id: res.data._id });
				}
			}
		} else {
			const res = await addEvent(formData);
			if (res.code === 200) {
				toast("Success", {
					description: "Job created successfully.",
				});
				setEvents((prevEvents) => [
					...prevEvents,
					{ ...res.data, id: res.data._id },
				]);
			}
		}

		open?.event?.view?.calendar?.unselect();
		setLoading(false);
		setFiles([]);

		if (close) handleClose(false);
	};

	// const handleSubmit = async ({ close = true }: { close?: boolean }) => {
	// 	const formData = new FormData();
	// 	const authenticatedUser = sessionStorage.getItem("authenticatedUser");
	// 	if (!authenticatedUser) return;
	// 	const user: User = JSON.parse(authenticatedUser);
	// 	const userId = user?._id;
	// 	const userName = user?.name;
	// 	const nickName = user?.nick_name;
	// 	if (!userId || !userName || !nickName || !canContinue()) return;
	// 	setLoading(true);
	// 	const start = moment(open?.event?.start);
	// 	const end = getEndTime();
	// 	let eventColor = colors.lessThanTwoHours;
	// 	const duration = moment.duration(end.diff(start)).asHours();
	// 	if (duration >= 2 && duration < 4) eventColor = colors.twoHours;
	// 	else if (duration >= 4 && duration < 6) eventColor = colors.fourHours;
	// 	else if (duration >= 6 && duration < 8) eventColor = colors.sixHours;
	// 	else if (duration === 8) eventColor = colors.eightHours;
	// 	else if (duration > 8) eventColor = colors.moreThanEightHours;

	// 	const newEvent: EventType = {
	// 		title: name,
	// 		start: start.toISOString(),
	// 		end: end?.toISOString(),
	// 		// email,
	// 		notes,
	// 		address,
	// 		contractorName,
	// 		phoneNumber: formatPhoneNumber(phoneNumber),
	// 		timeSlot,
	// 		color: eventColor,
	// 		userId,
	// 		userName,
	// 		files: images,
	// 		userNickname: nickName,
	// 	};

	// 	(Object.keys(newEvent) as (keyof EventType)[]).forEach((key) => {
	// 		const value = newEvent[key];
	// 		formData.append(
	// 			key,
	// 			key === "timeSlot" && value === null
	// 				? "null"
	// 				: key === "files"
	// 				? JSON.stringify(value)
	// 				: value
	// 				? String(value)
	// 				: ""
	// 		);
	// 	});
	// 	if (files.length > 0)
	// 		files.forEach((file) => {
	// 			formData.append("files", file);
	// 		});

	// 	if (open?.edit) {
	// 		const res = await editEvent(formData);
	// 		if (res.code === 200) {
	// 			toast("Success", {
	// 				description: "Job updated successfully.",
	// 			});
	// 			setEvents((prevEvents) =>
	// 				[...prevEvents].map((item) =>
	// 					item.id === open?.id ? { ...res.data, id: res.data._id } : item
	// 				)
	// 			);
	// 			if (copyUnsavedChanges) {
	// 				setCopyUnsavedChanges(false);
	// 				console.log("res", res.data);
	// 				await copyEventToClipboard({ ...res.data, id: res.data._id });
	// 			}
	// 		}
	// 	} else {
	// 		const res = await addEvent(formData);
	// 		if (res.code === 200) {
	// 			toast("Success", {
	// 				description: "Job created successfully.",
	// 			});
	// 			setEvents((prevEvents) => [
	// 				...prevEvents,
	// 				{ ...res.data, id: res.data._id },
	// 			]);
	// 		}
	// 	}

	// 	open?.event?.view?.calendar?.unselect();
	// 	setLoading(false);
	// 	setFiles([]);

	// 	if (close) handleClose(false);
	// };

	const handleDelete = async () => {
		if (!open?.id) return;
		setLoading(true);
		const response = await deleteEvent(open?.id);
		if (response.status === 200) {
			setEvents((prevEvents) =>
				prevEvents.filter((item) => item.id !== open?.id)
			);
			toast("Success", {
				description: "Job deleted successfully.",
			});
			setLoading(false);
			handleClose(false);
		} else
			toast.error("Error", {
				description: "Failed to delete Job.",
			});
		setLoading(false);
	};

	const exportJobButtonClick = () => {
		const event = events.find((item) => item._id === open?.id);
		if (
			name !== event?.title ||
			contractorName !== event?.contractorName ||
			notes !== event?.notes ||
			phoneNumber !== event?.phoneNumber ||
			address !== event?.address ||
			!areArraysEqual(images, event?.files ?? []) ||
			!areArraysEqual(event?.files ?? [], images) ||
			files?.length > 0 ||
			timeSlot !== event?.timeSlot
		) {
			setUnsavedChanges(true);
			return;
		}
		setExportMode(true);
	};

	const handleExport = async () => {
		const event = events.find((item) => item._id === open?.id);
		setLoading(true);
		const response = await exportEvent({
			title: event?.title,
			// email: event?.email,
			phoneNumber: event?.phoneNumber,
			contractorName: event?.contractorName,
			address: event?.address,
			notes: event?.notes,
			sendTo: selectUser?.phoneNumber,
			date: moment(event?.start).format("MM/DD/YYYY"),
			startTime: moment(event?.start).format("hh:mm A"),
			endTime: moment(event?.end).format("hh:mm A"),
		});
		if (response.status === 200) {
			toast("Success", {
				description: "Job exported successfully.",
			});
			setLoading(false);
			handleClose(false);
		} else
			toast.error("Error", {
				description: "Failed to export Job.",
			});
		setLoading(false);
	};

	const copyEventToClipboard = async (event: EventType | undefined) => {
		console.log("copyEventToClipboard", event);

		if (!event)
			return toast.error("Error", {
				description: "Failed to copy Job.",
			});
		const date = moment(event?.start).format("MM/DD/YYYY");
		const startTime = moment(event?.start).format("hh:mm A");
		const endTime = moment(event?.end).format("hh:mm A");
		const text = `Client: ${event?.title}${
			event?.phoneNumber?.trim() ? `\nPhone Number: ${event?.phoneNumber}` : ""
		}${
			event?.contractorName?.trim()
				? `\nContractor Name: ${event?.contractorName}`
				: ""
		}${event?.address?.trim() ? `\nAddress: ${event?.address}` : ""}${
			event?.notes?.trim()
				? `\nNotes: ${
						isHTML(event?.notes)
							? convertHTMLToPlainText(event?.notes)
							: event?.notes
				  }`
				: ""
		}${date.trim() ? `\n\nDate: ${date}` : ""}${
			startTime.trim() && endTime.trim()
				? `\nTime Slot: ${startTime} - ${endTime}`
				: ""
		}`;
		const response = await copyToClipboard(text);
		if (response)
			toast("Success", {
				description: "Job copied successfully.",
			});
		else
			toast.error("Error", {
				description: "Failed to copy Job.",
			});
	};

	const copyEvent = async (check = true) => {
		try {
			const event = events.find((item) => item._id === open?.id);
			if (
				check &&
				(name !== event?.title ||
					contractorName !== event?.contractorName ||
					notes !== event?.notes ||
					phoneNumber !== event?.phoneNumber ||
					address !== event?.address ||
					!areArraysEqual(images, event?.files ?? []) ||
					!areArraysEqual(event?.files ?? [], images) ||
					files?.length > 0 ||
					timeSlot !== event?.timeSlot)
			) {
				setCopyUnsavedChanges(true);
				return;
			}
			copyEventToClipboard(event);
		} catch (err) {
			toast.error("Error", {
				description: "Failed to copy Job.",
			});
			console.log("error", error);
		}
	};

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") closePopup();
		};
		if (open?.open) window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [open?.open, closePopup]);

	return (
		<AnimatePresence>
			{open?.open && (
				<motion.div
					className='absolute z-30 top-0 left-0 bg-black/30 h-screen w-screen flex flex-row justify-center items-center'
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.3, ease: "easeInOut" }}
					onClick={() => handleClose(true)}
				>
					<motion.div
						className={cn(
							"w-[90vw] sm:w-[60vw] md:w-[55vw] md:max-w-[550px] lg:w-[35vw] lg:min-w-[550px] bg-white rounded-2xl p-3 px-5 overflow-y-auto max-h-[95vh] h-fit",
							exportMode && "lg:w-[35vw]"
						)}
						initial={{ opacity: 0, y: -50 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -50 }}
						transition={{ duration: 0.3, ease: "easeInOut" }}
						onClick={(e) => e.stopPropagation()}
					>
						<div className='flex flex-row justify-between items-center pb-3 border-b border-gray-200'>
							<h1 className='font-medium text-2xl sm:text-2xl md:text-3xl lg:text-4xl text-themeColor '>
								{exportMode ? "Export" : open?.edit ? "Update" : "Create"} Job
							</h1>
							<button
								className='text-gray-500 hover:text-black duration-200 w-fit h-fit hover:bg-slate-100 rounded-full p-0.5'
								onClick={() => handleClose(true)}
							>
								<X />
							</button>
						</div>
						{exportMode ? (
							<div className='pt-1'>
								<h1 className='mt-6 mb-4 font-medium text-base text-themeColor'>
									Select user you want to export the job details to:
								</h1>
								<Select
									value={selectUser?.name}
									onValueChange={(value) => {
										const contractor = contractors.find(
											(item) => item.name === value
										);
										if (contractor) setSelectUser(contractor);
									}}
								>
									<SelectTrigger className='h-12 w-[85%] m-auto hover:bg-blue-50 duration-150 cursor-pointer data-[placeholder]:text-gray-500 bg-gray-50'>
										<SelectValue placeholder='Select user' />
									</SelectTrigger>
									<SelectContent className='bg-white max-h-48 overflow-y-auto'>
										{contractors.map((contractor) => (
											<SelectItem
												value={contractor.name}
												className='h-10 bg-gray-50 hover:bg-blue-50 duration-150 cursor-pointer mb-1'
											>
												{contractor.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<div className='pt-4 flex flex-row justify-end items-center gap-x-2 mt-6'>
									<button
										className='border-[1.5px] border-themeColor rounded-lg p-2 sm:p-2 sm:px-6 text-themeColor bg-themeColor/10 hover:bg-themeColor/20 duration-200 overflow-hidden text-sm sm:text-base'
										onClick={() => setExportMode(false)}
									>
										Back
									</button>
									<button
										className={cn(
											"border-[1.5px] border-themeColor rounded-lg p-2 sm:p-2 text-white bg-themeColor hover:bg-themeColor/80 duration-200 overflow-hidden text-sm sm:text-base truncate",
											!selectUser &&
												"bg-themeColor/60 hover:bg-themeColor/60 border-themeColor/40"
										)}
										onClick={handleExport}
										disabled={!selectUser || loading}
									>
										Export Job
									</button>
								</div>
							</div>
						) : (
							<div className='mt-1 overflow-y-auto'>
								<InputField
									label={"Customer Name"}
									placeholder={"Enter customer name"}
									value={name}
									setValue={setName}
								/>
								{/* <InputField
									label={"Customer Email"}
									placeholder={"Enter customer email"}
									value={email}
									type={InputType.email}
									error={error}
									setValue={handleTextChange(InputType.email)}
								/> */}
								<InputField
									label={"Customer Phone Number"}
									placeholder={"Enter customer phone number"}
									type={InputType.phone}
									error={error}
									value={phoneNumber}
									setValue={handleTextChange(InputType.phone)}
								/>
								<InputField
									label={"Contractor Name"}
									placeholder={"Enter contractor name"}
									value={contractorName}
									setValue={setContractorName}
								/>
								<InputField
									label={"Job Address"}
									placeholder={"Enter job address"}
									value={address}
									setValue={setAddress}
								/>
								<h3 className='text-base text-themeColor font-semibold mt-2'>
									Notes
								</h3>
								<div className='my-2 w-full overflow-hidden'>
									<TiptapRichTextEditor
										value={notes}
										setValue={(value) => setNotes(value)}
										className='h-28'
									/>
								</div>
								<div className='flex flex-row justify-between items-center mt-2 text-base text-themeColor'>
									<h3 className='font-semibold'>Select Time Slot</h3>
									<p className='text-sm sm:text-base font-medium'>{`(${
										open?.event?.start
											? moment(open?.event?.start).format("hh:mm A")
											: " -- : -- "
									} to ${
										open?.event?.end
											? moment(getEndTime()).format("hh:mm A")
											: " -- : -- "
									})`}</p>
								</div>
								<div className='mt-1 gap-2 flex flex-row flex-wrap justify-start items-center'>
									<TimeSlot
										text='2 Hours'
										select={timeSlot}
										setSelect={setTimeSlot}
									/>
									<TimeSlot
										text='4 Hours'
										select={timeSlot}
										setSelect={setTimeSlot}
									/>
									<TimeSlot
										text='6 Hours'
										select={timeSlot}
										setSelect={setTimeSlot}
									/>
									<TimeSlot
										text='8 Hours'
										select={timeSlot}
										setSelect={setTimeSlot}
									/>
								</div>
								<h3 className='text-base text-themeColor font-semibold my-2'>
									Upload Images (Max 5)
								</h3>
								<div className='flex flex-row justify-start items-center gap-x-2 '>
									<FileUpload
										files={files}
										images={images}
										uploadFiles={selectFiles}
									/>
									{images?.map((file: Images) => (
										<ImageTile
											key={file.url}
											image={file}
											canRemove={
												events.find((item) => item?._id === open?.id)
													?.userId === authenticatedUser?._id
											}
											onRemove={() =>
												setImages((prev) =>
													prev.filter(
														(item) =>
															!(
																item.filename === file.filename &&
																item.url === file.url
															)
													)
												)
											}
										/>
									))}
									{files?.map((file: File, index) => (
										<ImageTile
											key={file.lastModified}
											file={file}
											canRemove={
												events.find((item) => item?._id === open?.id)
													?.userId === authenticatedUser?._id
											}
											onRemove={() =>
												setFiles((prev) =>
													prev.filter(
														(item, filterIndex) =>
															!(
																item.lastModified === file.lastModified &&
																item.name === file.name &&
																index === filterIndex
															)
													)
												)
											}
										/>
									))}
								</div>
								<div className='mt-2 flex flex-row justify-between items-center'>
									<div className='flex flex-row justify-start items-center gap-x-2'>
										{open?.edit && (
											<>
												<button
													className={cn(
														"w-fit h-fit rounded-lg p-2 hover:bg-gray-100 text-destructive",
														loading &&
															"text-destructive/60 hover:text-destructive/60"
													)}
													onClick={() => setOpenConfirmation(true)}
													disabled={loading}
												>
													<Trash2 className='w-5 h-5 min-h-5 min-w-5 max-h-5 max-w-5' />
												</button>
												<button
													className={cn(
														"w-fit h-fit rounded-lg p-2 hover:bg-gray-100 text-themeColor",
														loading &&
															"text-themeColor/60 hover:text-themeColor/60"
													)}
													onClick={exportJobButtonClick}
													disabled={loading}
												>
													<Upload className='w-5 h-5 min-h-5 min-w-5 max-h-5 max-w-5 ' />
												</button>
												<button
													className={cn(
														"w-fit h-fit rounded-lg p-2 hover:bg-gray-100 text-themeColor",
														loading &&
															"text-themeColor/60 hover:text-themeColor/60"
													)}
													onClick={() => copyEvent(true)}
													disabled={loading}
												>
													<Copy className='w-5 h-5 min-h-5 min-w-5 max-h-5 max-w-5 ' />
												</button>
											</>
										)}
									</div>
									<div className='flex flex-row justify-start items-center gap-x-2'>
										<button
											className='border-[1.5px] border-themeColor rounded-lg p-2 sm:p-2 sm:px-4 text-themeColor bg-themeColor/10 hover:bg-themeColor/20 duration-200 overflow-hidden text-sm sm:text-base'
											onClick={() => handleClose(true)}
										>
											Cancel
										</button>
										<button
											className={cn(
												"border-[1.5px] border-themeColor rounded-lg p-2 sm:p-2 text-white bg-themeColor hover:bg-themeColor/80 duration-200 overflow-hidden text-sm sm:text-base truncate",
												!canContinue() &&
													"bg-themeColor/60 hover:bg-themeColor/60 border-themeColor/40"
											)}
											onClick={() => handleSubmit({ close: true })}
											disabled={!canContinue()}
										>
											{open?.edit ? "Update" : "Create"} Job
										</button>
									</div>
								</div>
							</div>
						)}
					</motion.div>
					{openConfirmation && (
						<ConfirmationModal
							title={"Confirmation"}
							description={"Are you sure you want to delete this job?"}
							yesFunc={handleDelete}
							handleClose={() => setOpenConfirmation(false)}
							onClose={() => setOpenConfirmation(false)}
						/>
					)}
					{(unsavedChanges || closeUnsavedChanges || copyUnsavedChanges) && (
						<ConfirmationModal
							title={"Confirmation"}
							description={`You have unsaved changes. ${
								unsavedChanges
									? "Do you want to save the changes before exporting the job or proceed without saving?"
									: copyUnsavedChanges
									? "Do you want to save them before copying?"
									: "Do you want to save them before closing?"
							}`}
							yesButtonText={unsavedChanges ? "Save Changes" : "Yes"}
							cancelButtonText={unsavedChanges ? "Proceed" : "No"}
							yesFunc={async () => await handleSubmit({ close: false })}
							isCopy={copyUnsavedChanges}
							onClose={() => {
								if (unsavedChanges) setUnsavedChanges(false);
								else if (closeUnsavedChanges) setCloseUnsavedChanges(false);
								else setCopyUnsavedChanges(false);
							}}
							handleClose={() => {
								if (unsavedChanges) {
									setExportMode(true);
									setUnsavedChanges(false);
								} else if (closeUnsavedChanges) {
									setCloseUnsavedChanges(false);
									handleClose(false);
								} else {
									setCopyUnsavedChanges(false);
									copyEvent(false);
								}
							}}
						/>
					)}
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export default CreateEventModal;

function InputField({
	label,
	placeholder,
	value,
	setValue,
	type,
	error,
}: {
	label: string;
	placeholder: string;
	value: string;
	type?: InputType;
	error?: {
		email: string | null;
		phone: string | null;
	};
	setValue: Dispatch<SetStateAction<string>> | ((e: string) => void);
}) {
	return (
		<>
			<h3
				className={cn(
					"text-base text-themeColor font-semibold mt-1",
					"md:mt-2"
				)}
			>
				{label}
			</h3>
			<input
				placeholder={placeholder}
				value={value}
				onChange={(e) => setValue(e.target.value)}
				className={cn(
					"border w-full bg-slate-100 rounded-lg h-8 pl-2 mt-1 text-base",
					"md:h-10 md:pl-3 md:mt-1 md:text-base"
				)}
			/>
			{((type === InputType.email && error && error.email) ||
				(type === InputType.phone && error && error.phone)) && (
				<span className='text-destructive '>
					Error:{" "}
					{type === InputType.email && error.email ? error.email : error.phone}
				</span>
			)}
		</>
	);
}

function TimeSlot({
	text,
	select,
	setSelect,
}: {
	text: "2 Hours" | "4 Hours" | "6 Hours" | "8 Hours";
	select: "2 Hours" | "4 Hours" | "6 Hours" | "8 Hours" | null;
	setSelect: Dispatch<
		SetStateAction<"2 Hours" | "4 Hours" | "6 Hours" | "8 Hours" | null>
	>;
}) {
	return (
		<button
			className={cn(
				"text-base border-[1.5px] border-themeColor rounded-full w-20 p-[0.35rem] text-themeColor bg-themeColor/5 hover:bg-themeColor/80 hover:text-white duration-200 truncate",
				select === text && "bg-themeColor text-white hover:bg-themeColor"
			)}
			onClick={() => setSelect(text === select ? null : text)}
		>
			<p>{text}</p>
		</button>
	);
}

const FileUpload = ({
	files,
	images,
	uploadFiles,
}: {
	files: File[];
	images: Images[];
	uploadFiles: (files: File[]) => void;
}) => {
	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			if (images.length + files.length + acceptedFiles.length > 5) {
				toast.error("Error", {
					description: "You can not select more than 5 images",
				});
				return;
			} else {
				if (acceptedFiles.length > 0) uploadFiles(acceptedFiles);
			}
		},
		[files, images, uploadFiles]
	);

	const { getRootProps, getInputProps, open } = useDropzone({
		onDrop,
		accept: { "image/*": [] },
		multiple: true,
		maxFiles: 5,
		noClick: true,
		// disabled: [...images, ...files].length >= 5,
	});
	const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.stopPropagation();
		e.preventDefault();
		if ([...images, ...files].length >= 5) {
			return toast.error("Error", {
				description: "You cannot select more than 5 images",
			});
		} else open();
	};

	return (
		<Button
			className='hover:text-themeColor p-0 w-12 h-12'
			variant={"outline"}
			{...getRootProps({ onClick: (e) => handleClick(e) })}
		>
			<input {...getInputProps()} />
			<ImagePlus className='min-w-6 min-h-6' />
		</Button>
	);
};
